/**
 * JIRA Project Mapping Component
 *
 * Displays and manages JIRA project mapping for a Nivoon project.
 * Allows Project Managers and above to map projects to JIRA.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getJiraConfigurationStatus,
  getProjectMappings,
  createProjectMapping,
  updateProjectMapping,
  deleteProjectMapping,
  getJiraProjects,
  getJiraIssueTypes,
  getJiraPriorities,
  JiraProjectMapping as MappingType
} from '../services/jiraService';

interface JiraProjectMappingProps {
  projectId: string;
}

export function JiraProjectMapping({ projectId }: JiraProjectMappingProps) {
  const { token, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [configExists, setConfigExists] = useState(false);
  const [mapping, setMapping] = useState<MappingType | null>(null);
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem(`projectDetail_jiraMapping_expanded`);
    return saved !== null ? saved === 'true' : false;
  });

  const [isCreatingMapping, setIsCreatingMapping] = useState(false);
  const [jiraProjects, setJiraProjects] = useState<any[]>([]);
  const [issueTypes, setIssueTypes] = useState<any[]>([]);
  const [priorities, setPriorities] = useState<any[]>([]);
  const [loadingJiraData, setLoadingJiraData] = useState(false);

  const [formData, setFormData] = useState({
    jira_project_key: '',
    default_issue_type: 'Task',
    default_priority: 'Medium',
    auto_sync_enabled: true
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const canManageMapping =
    user?.role === 'project_manager' ||
    user?.role === 'company_admin' ||
    user?.role === 'platform_admin';

  const canConfigureCompanyJira =
    user?.role === 'company_admin' ||
    user?.role === 'platform_admin';

  useEffect(() => {
    loadData();
  }, [projectId]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      // Check if JIRA is configured (uses status endpoint accessible to all authenticated users)
      const statusResponse = await getJiraConfigurationStatus(token!);
      const isConfigured = statusResponse.success && statusResponse.data?.configured;
      setConfigExists(isConfigured);

      if (isConfigured) {
        // Load existing mapping (404 is expected if no mapping exists)
        try {
          const mappingsResponse = await getProjectMappings(projectId, token!);
          if (mappingsResponse.success && mappingsResponse.data) {
            setMapping(mappingsResponse.data);
          }
        } catch (mappingErr: any) {
          // 404 is expected when no mapping exists yet, don't treat as error
          if (mappingErr.response?.status !== 404) {
            console.error('Error loading JIRA mapping:', mappingErr);
          }
        }
      }
    } catch (err: any) {
      console.error('Error loading JIRA configuration status:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleLoadJiraProjects() {
    try {
      setLoadingJiraData(true);
      setError(null);

      const [projectsRes, prioritiesRes] = await Promise.all([
        getJiraProjects(token!),
        getJiraPriorities(token!)
      ]);

      if (projectsRes.success) {
        setJiraProjects(projectsRes.data);
      }

      if (prioritiesRes.success) {
        setPriorities(prioritiesRes.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load JIRA data');
    } finally {
      setLoadingJiraData(false);
    }
  }

  async function handleProjectKeyChange(projectKey: string) {
    setFormData({ ...formData, jira_project_key: projectKey });

    // Load issue types for selected project
    if (projectKey) {
      try {
        const issueTypesRes = await getJiraIssueTypes(projectKey, token!);
        if (issueTypesRes.success) {
          setIssueTypes(issueTypesRes.data);
          // Set first issue type as default if available
          if (issueTypesRes.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              default_issue_type: issueTypesRes.data[0].name
            }));
          }
        }
      } catch (err) {
        console.error('Error loading issue types:', err);
      }
    }
  }

  async function handleCreateMapping() {
    try {
      setError(null);
      const response = await createProjectMapping(
        projectId,
        {
          project_id: projectId,
          ...formData
        },
        token!
      );

      if (response.success) {
        setSuccess('Project mapped to JIRA successfully');
        setMapping(response.data);
        setIsCreatingMapping(false);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create mapping');
    }
  }

  async function handleDeleteMapping() {
    if (!mapping) return;

    if (!window.confirm('Are you sure you want to remove this JIRA mapping? This will not delete the JIRA project, but requirements will no longer sync.')) {
      return;
    }

    try {
      setError(null);
      await deleteProjectMapping(projectId, mapping.id, token!);
      setSuccess('Mapping removed successfully');
      setMapping(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete mapping');
    }
  }

  function handleExpandToggle() {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem(`projectDetail_jiraMapping_expanded`, String(newState));
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading JIRA configuration...</p>
      </div>
    );
  }

  if (!configExists) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">JIRA Integration</h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          {canConfigureCompanyJira ? (
            <p className="text-sm text-yellow-800">
              JIRA integration is not configured for your company.{' '}
              <a href="/company/jira" className="underline hover:text-yellow-900">
                Configure JIRA
              </a>
            </p>
          ) : (
            <p className="text-sm text-yellow-800">
              JIRA integration is not configured for your company. Please contact your Company Admin to set up the JIRA connection before mapping projects.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      {/* Collapsible Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={handleExpandToggle}
      >
        <div className="flex items-center gap-2">
          <svg
            className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
              isExpanded ? 'rotate-90' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">JIRA Integration</h2>
        </div>
        {mapping && (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            Mapped
          </span>
        )}
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {/* Success/Error Messages */}
          {error && (
            <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 rounded-md bg-green-50 border border-green-200">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {/* Existing Mapping Display */}
          {mapping && !isCreatingMapping && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    JIRA Project
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-50">
                    {mapping.jira_project_name} ({mapping.jira_project_key})
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Issue Type
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-50">{mapping.default_issue_type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Priority
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-50">{mapping.default_priority}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Auto-Sync
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-50">
                    {mapping.auto_sync_enabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>

              {canManageMapping && (
                <div className="flex items-center gap-3 pt-4">
                  <button
                    onClick={handleDeleteMapping}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-red-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  >
                    Remove Mapping
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Create Mapping Form */}
          {!mapping && canManageMapping && (
            <div className="space-y-4">
              {!isCreatingMapping ? (
                <button
                  onClick={() => {
                    setIsCreatingMapping(true);
                    handleLoadJiraProjects();
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Map to JIRA Project
                </button>
              ) : (
                <div className="space-y-4">
                  {loadingJiraData ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading JIRA projects...</p>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          JIRA Project <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.jira_project_key}
                          onChange={(e) => handleProjectKeyChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required
                        >
                          <option value="">Select a JIRA project</option>
                          {jiraProjects.map((proj) => (
                            <option key={proj.id} value={proj.key}>
                              {proj.name} ({proj.key})
                            </option>
                          ))}
                        </select>
                      </div>

                      {issueTypes.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Default Issue Type
                          </label>
                          <select
                            value={formData.default_issue_type}
                            onChange={(e) =>
                              setFormData({ ...formData, default_issue_type: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {issueTypes.map((type) => (
                              <option key={type.id} value={type.name}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {priorities.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Default Priority
                          </label>
                          <select
                            value={formData.default_priority}
                            onChange={(e) =>
                              setFormData({ ...formData, default_priority: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {priorities.map((priority) => (
                              <option key={priority.id} value={priority.name}>
                                {priority.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="auto_sync"
                          checked={formData.auto_sync_enabled}
                          onChange={(e) =>
                            setFormData({ ...formData, auto_sync_enabled: e.target.checked })
                          }
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="auto_sync" className="text-sm text-gray-700 dark:text-gray-300">
                          Enable automatic bidirectional sync
                        </label>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleCreateMapping}
                          disabled={!formData.jira_project_key}
                          className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 transition-colors"
                        >
                          Create Mapping
                        </button>
                        <button
                          onClick={() => setIsCreatingMapping(false)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {!mapping && !canManageMapping && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Only Project Managers and above can map projects to JIRA.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
